import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { getAdvisorAnswer, advisorQA } from '@/data/mockData';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const ChatPanel = ({ compact = false }) => {
  const { t, lang, data } = useApp();
  const [messages, setMessages] = useState([{ role: 'advisor', text: t('advisor.greeting') }]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);
  const suggestions = (advisorQA[lang] || advisorQA.es).map((x) => x.q);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);

  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q || thinking) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      const a = getAdvisorAnswer(lang, q);
      setMessages((m) => [...m, { role: 'advisor', text: a }]);
      setThinking(false);
    }, 900);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="chat-panel">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className="flex gap-2 max-w-[85%]">
              {m.role === 'advisor' && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={`px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                m.role === 'user'
                  ? 'bg-zinc-900 text-white rounded-tr-sm'
                  : 'bg-cyan-50 border border-cyan-100 text-zinc-800 rounded-tl-sm'
              }`}>
                {m.text}
              </div>
            </div>
          </motion.div>
        ))}
        <AnimatePresence>
          {thinking && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2 text-zinc-400 text-sm pl-9">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('advisor.thinking')}
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-2">{t('advisor.suggestions')}</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, compact ? 3 : 5).map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`advisor-suggestion-${i}`}
                className="text-xs px-3 py-1.5 rounded-full border border-zinc-200 text-zinc-600 hover:border-cyan-300 hover:text-cyan-700 hover:bg-cyan-50 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-zinc-200">
        <div className="flex items-center gap-2 h-11 pl-4 pr-1.5 rounded-full bg-zinc-100 focus-within:bg-white focus-within:ring-2 focus-within:ring-cyan-200 transition-all">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder={t('advisor.placeholder')}
            data-testid="advisor-input"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-400"
          />
          <button
            onClick={() => send()}
            data-testid="advisor-send"
            disabled={!input.trim()}
            className="w-8 h-8 rounded-full bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 text-white flex items-center justify-center transition-colors shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
